import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";
import { useAuth } from "../../context/AuthContext";
import { usePermissions } from "../../shared/hooks/usePermissions";

const DEFAULT_ROUTES = {
  super_admin: "/admin",
  platform_staff: "/admin",
  hospital_admin: "/reception",
  branch_admin: "/reception",
  staff: "/reception",
  receptionist: "/reception",
  pharmacist: "/pharmacist-dashboard",
  doctor: "/doctor-dashboard",
  pet_owner: "/pet-owner-dashboard",
};
const SUBSCRIPTION_PENDING_ALLOWED_PATHS = ["/dashboard", "/notifications", "/subscription"];
const SUBSCRIPTION_INACTIVE_ALLOWED_PATHS = ["/dashboard", "/notifications", "/subscription", "/settings"];

const normalizeRole = (role) => `${role || ""}`.trim().toLowerCase();

const ProtectedRoute = ({ allowedRoles = [], requiredPermissions = [], children }) => {
  const location = useLocation();
  const { sessionReady, subscriptionState, userLoggedIn, userData } = useAuth();
  const { canAll } = usePermissions();

  if (!sessionReady) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!userLoggedIn) {
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }

  const currentRole = normalizeRole(userData?.role || userData?.profile_type);
  const normalizedAllowedRoles = allowedRoles.map(normalizeRole);
  const shouldCheckSubscription = !["super_admin", "platform_staff", "pet_owner"].includes(currentRole);
  const hasPendingSubscription =
    normalizeRole(subscriptionState?.status) === "pending_activation" &&
    shouldCheckSubscription;
  const hasInactiveSubscription =
    ["past_due", "expired", "cancelled", "suspended"].includes(normalizeRole(subscriptionState?.status)) &&
    shouldCheckSubscription;

  if (shouldCheckSubscription && subscriptionState?.loading) {
    return (
      <Box sx={{ minHeight: "60vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (normalizedAllowedRoles.length > 0 && !normalizedAllowedRoles.includes(currentRole)) {
    const fallbackPath = DEFAULT_ROUTES[currentRole] || "/signin";
    return <Navigate to={fallbackPath === location.pathname ? "/signin" : fallbackPath} replace />;
  }

  if (requiredPermissions.length > 0 && !canAll(requiredPermissions)) {
    const fallbackPath = DEFAULT_ROUTES[currentRole] || "/signin";
    return <Navigate to={fallbackPath === location.pathname ? "/signin" : fallbackPath} replace />;
  }

  if (
    hasPendingSubscription &&
    !SUBSCRIPTION_PENDING_ALLOWED_PATHS.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
  ) {
    const fallbackPath = currentRole === "hospital_admin" ? "/subscription" : "/dashboard";
    return <Navigate to={fallbackPath === location.pathname ? "/signin" : fallbackPath} replace />;
  }

  if (
    hasInactiveSubscription &&
    !SUBSCRIPTION_INACTIVE_ALLOWED_PATHS.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`))
  ) {
    const fallbackPath = currentRole === "hospital_admin" ? "/subscription" : "/dashboard";
    return <Navigate to={fallbackPath === location.pathname ? "/signin" : fallbackPath} replace />;
  }

  return children;
};

export default ProtectedRoute;
